<template>
  <div
    class="no-quote-container b2b-tc-journey__container white rounded-lg elevation-3 ma-0 ma-md-4 ma-xl-0 pb-3 pt-1 pb-7"
  >
    <div class="text-center pb-0">
      <h3
        class="font-weight-bold mb-4 mt-6"
        :class="$vuetify.breakpoint.lgAndUp ? 'font-24' : 'font-28'"
      >
        Sorry!
      </h3>
      <p class="sorry-description font-16 font-weight-regular mt-4 mb-5">
        Paymentshield are unable to provide a Tenants Contents Insurance quote
        based on the property risk profile.
        <span v-if="canSellTLI"
          >Alternatively, you can find a quote below for Essential Cover
          (Tenants Liability Insurance).</span
        >
      </p>
    </div>
    <div v-if="canSellTLI" class="text-center essential-cover-card pt-0">
      <div class="d-flex justify-center">
        <div class="insurance-card">
          <v-card
            class="card-title font-weight-bold text-center pa-2 primary--text"
          >
            Essential Cover - 12 Months
          </v-card>
          <v-card-subtitle
            class="card-subtitle mt-6 font-24 pb-4 px-6 font-weight-bold text-left"
          >
            Tenants Liability Insurance
          </v-card-subtitle>
          <v-card-text class="text-center pl-6 pr-6">
            <p class="description-text mb-4 font-18 text-left">
              £10,000 of Tenant’s Liability cover and £100,000 of Legal Expenses
              cover
            </p>

            <v-row
              justify="center"
              no-gutters
              class="flex-column-reverse flex-lg-row"
            >
              <v-col cols="12" lg="7" class="price-column mt-4 text-left">
                <p class="price-text">
                  {{ annualPrice | formatCurrency }}
                </p>
                <div class="payment-info font-16">
                  <p class="monthly mt-1 mb-1 font-weight-bold text-left">
                    One off payment
                  </p>
                  <BaseButton
                    @click="showBreakdown"
                    class="px-0 primary--text opacity-80"
                    depressed
                    flat
                    link
                    ripple
                    text="View a breakdown"
                  />
                </div>
              </v-col>
              <v-col
                cols="12"
                lg="5"
                class="text-right mt-1 mt-lg-0 mb-5 mb-lg-0"
              >
                <v-img
                  width="144"
                  :src="require('@/assets/images/property-guard-logo.png')"
                  transition="scale-transition"
                  max-width="144"
                  alt=""
                  class="img"
                />
              </v-col>
            </v-row>

            <v-btn
              depressed
              x-large
              rounded
              color="psorange"
              class="apply-btn font-18 font-weight-bold mt-2 mb-4 mb-lg-6 primary--text"
              @click="applyEssentialCover"
              aria-label="Apply for Essential Cover"
            >
              {{
                $vuetify.breakpoint.lgAndUp
                  ? "APPLY FOR ESSENTIAL COVER"
                  : "APPLY"
              }}
            </v-btn>

            <div class="text-left">
              <p
                class="coverage font-16 font-weight-bold mb-lg-1 text-center text-lg-left"
                :class="[
                  $vuetify.breakpoint.lgAndUp ? '' : 'covered-expand-mobile',
                  expandCoveredContent ? 'mb-6' : 'mb-3',
                ]"
                @click="openCoveredContentMobile(!$vuetify.breakpoint.lgAndUp)"
              >
                What’s included?
              </p>

              <template
                v-if="expandCoveredContent || $vuetify.breakpoint.lgAndUp"
              >
                <ul class="cover-list pa-0">
                  <li class="cover-check d-flex justify-center mt-3">
                    <v-icon left class="green--text align-start"
                      >mdi-check</v-icon
                    >
                    <p>
                      Protection from costs incurred from accidental damage
                      caused to your landlord’s property that you’re legally
                      responsible for, such as fixtures and fittings or
                      furniture where provided. You must report each claim as
                      soon as possible and no later than 60 days after the event
                      has occurred.
                    </p>
                  </li>
                  <li class="cover-check d-flex justify-center">
                    <v-icon left class="green--text align-start"
                      >mdi-check</v-icon
                    >
                    <p>
                      Flexible terms - select either 6 or 12 months' cover
                      depending on your tenancy
                    </p>
                  </li>
                  <li class="cover-check d-flex justify-center">
                    <v-icon left class="green--text align-start"
                      >mdi-check</v-icon
                    >
                    <p>
                      Dedicated customer support should you need to make a claim
                    </p>
                  </li>
                </ul>
              </template>
            </div>
          </v-card-text>
        </div>
      </div>
    </div>
    <!-- premium breakdown dialog -->
    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          insurancePremiumTitle="Tenants Liability premium (ex IPT)"
          :breakdownModel="data"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { mapActions, mapGetters, mapState } from "vuex";
import catalogueService from "@/services/catalogue.service";
import { PRODUCT } from "@/constants/common.constants";
import { AMOUNT_INSURED_FORMAT } from "@/constants/currency-format.constants";
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { prepareQuotes } from "@/common/pricing";
import { NOTAPPLICABLE, EXCLUDED } from "@/constants/common.constants.js";
export default {
  name: "TenantsNoQuoteResultPage",
  components: {
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/DialogContentPremiumBreakdown.vue"),
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      AMOUNT_INSURED_FORMAT,
      monthlyPrice: 0,
      annualPrice: 0,
      expandCoveredContent: false,
      canSellTLI: false,
      pricingResponse: {},
      quote: {
        IptRate: 0.12,
        CoverSelections: {
          BuildingsAD: NOTAPPLICABLE,
          ContentsAD: EXCLUDED,
        },
        QuestionSetId: 180,
        Quotes: [
          {
            InsurerName: "Property Guard",
            Cover: {
              IsRequested: true,
            },
            Premiums: [],
          },
        ],
        Prices: [],
      },
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "hasQuotes",
      "httpConfig",
      "branchNumber",
    ]),
    ...mapState(QUESTIONS_MODULE, ["products"]),
  },
  mounted() {
    this.getPricing();
    this.canSellTLI = this.products.some(
      (item) =>
        item.WebProductGroupId === PRODUCT.Tenants_Liability &&
        item.Branches.some((branch) => branch.BranchNo === this.branchNumber)
    );
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, {
      applyForEssentialCover: "applyForEssentialCover",
    }),
    async getPricing() {
      try {
        const response = await catalogueService.getProductPricing(
          PRODUCT.Tenants_Liability,
          this.httpConfig
        );
        this.pricingResponse = response;
        this.monthlyPrice =
          response?.MonthlyPrice?.Values?.TotalCostPerPaymentPeriod ?? 0;

        this.annualPrice =
          response?.AnnualPrice?.Values?.TotalCostPerPaymentPeriod ?? 0;
      } catch (error) {
        console.log(error);
      }
    },
    openCoveredContentMobile(isMobileExpand) {
      if (!isMobileExpand) {
        return;
      }
      this.expandCoveredContent = !this.expandCoveredContent;
    },
    async applyEssentialCover() {
      await this.applyForEssentialCover(PRODUCT.Tenants_Liability);
    },
    showBreakdown() {
      const premium = {
        PaymentMethod: "DirectDebit",
        PaymentFrequency: "Annual",
        PaymentFrequencyDescription: "Annual",
        PriceID: this.pricingResponse.AnnualPrice.ID,
      };

      this.quote.Quotes[0].Premiums.push(premium);
      this.quote.Prices.push(this.pricingResponse.AnnualPrice);
      const iterableModels = prepareQuotes(
        this.quote,
        this.quote.CoverSelections,
        0
      );

      this.showPremiumBreakdownAnnual(
        iterableModels.find((x) => x.name == "Property Guard").selectedQuote,
        "TENANTS_LIABILITY",
        PRODUCT.Tenants_Liability
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/d2c/journey.scss";
.no-quote-container {
  color: $black-2;
  margin-top: -28px !important;
}
.text--gray-dark {
  color: $gray-dark;
}

.sorry-description {
  line-height: 19.5px;
  max-width: 821px;
  margin: 0 auto;
}
.insurance-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  .covered-expand-mobile {
    color: $cyan-darken-4 !important;
    text-decoration: underline;
    &:hover {
      cursor: pointer;
    }
  }
}

.v-card {
  box-shadow: none !important;
}

.v-application .elevation-3 {
  box-shadow: none !important;
}

.card-title {
  background: rgba(250, 181, 25, 0.5);
  box-shadow: none !important;
  border-bottom-left-radius: 0%;
  border-bottom-right-radius: 0%;
}

.description-text {
  color: rgba(39, 39, 39, 0.8);
  line-height: 19.5px;
}

.sorry-description {
  line-height: 19.5px;
}

.price-text {
  font-size: 2.5rem;
  color: #272727;
  font-weight: bold;
  display: contents;
}

.payment-info {
  color: #757575;
}

.apply-button:hover {
  background-color: #ffca28;
}

.coverage {
  font-size: 0.9rem;
  color: #272727;
}
.text-info {
  color: rgba(39, 39, 39, 0.8);
}

.cover-check {
  color: #000000;
}
.monthly-text {
  color: rgba(39, 39, 39, 0.8);
}

.monthly {
  color: #272727;
}

.cover-list {
  list-style-type: none;
}
.apply-btn {
  width: 100%;
}

@media screen and (max-width: ($grid-breakpoints-lg + 1)) {
  .no-quote-container {
    .back-btn {
      margin-left: -15px !important;
    }
    .section-title {
      padding-left: 8px;
    }
    .info__wrapper {
      padding: 74px 40px 96px;
      color: #000;
    }
  }
}

@media screen and (min-width: ($grid-breakpoints-lg)) {
  .insurance-card {
    max-width: 454px;
  }
}
</style>
